<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import Repositories from '@/repositories';
const UsersRepository = Repositories.get('users');
import HeaderContentLayout from '@/router/layouts/header-content-layout';

/**
 * Dashboard Component
 */
export default {
  components: {
    Loading,
    HeaderContentLayout,
  },
  data() {
    return {
      isLoading: false,
      currentAccessToggle: false,
      user: null,
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;

      UsersRepository.customREST
        .getWithConfig(this.$route.params.idPublic)
        .then((response) => {
          this.user = response.data;

          this.currentAccessToggle =
            this.user.permissions.hasInventoryAccess &&
            this.user.permissions.hasReceptionsAccess &&
            this.user.permissions.hasOrdersAccess &&
            this.user.permissions.hasShoppingCartAccess &&
            this.user.permissions.hasDashboardsAccess &&
            this.user.permissions.hasWorkOrdersAccess &&
            this.user.permissions.hasConfigAccess;

          this.isLoading = false;
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
          this.isLoading = false;
        });
    },
    toggleAllHasAccess() {
      this.currentAccessToggle = !this.currentAccessToggle;

      this.user.permissions.hasInventoryAccess = this.currentAccessToggle;
      this.user.permissions.hasReceptionsAccess = this.currentAccessToggle;
      this.user.permissions.hasOrdersAccess = this.currentAccessToggle;
      this.user.permissions.hasShoppingCartAccess = this.currentAccessToggle;
      this.user.permissions.hasDashboardsAccess = this.currentAccessToggle;
      this.user.permissions.hasWorkOrdersAccess = this.currentAccessToggle;
      this.user.permissions.hasConfigAccess = this.currentAccessToggle;
    },
    cancel() {
      this.$router.push({ name: 'usersConfig' });
    },
    async edit() {
      this.isLoading = true;

      try {
        await UsersRepository.update(this.user, this.$route.params.idPublic);
        await this.$store.dispatch(
          'user/fetchUserConfig',
          this.$store.state.user.config.idPublic
        );
        this.$router.push({ name: 'usersConfig' });
      } catch (error) {
        this.$toast.errorToast(this.$t('errors.errorLoading'));
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <HeaderContentLayout :title="$t('users.user')">
    <template v-slot:header>
      <b-button @click="edit()" class="mr-2" variant="success">{{
        $t('general.save')
      }}</b-button>
      <b-button
        @click="cancel()"
        tabindex="-1"
        type="button"
        variant="secondary"
        >{{ $t('general.cancel') }}</b-button
      >
    </template>

    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>

    <template v-if="user">
      <h2 class="mb-4">{{ user.name }}</h2>

      <div class="mb-4">
        <b-form-group
          id="input-group-email"
          :label="$t('general.email')"
          label-for="input-email"
        >
          <b-form-input
            id="input-email"
            v-model="user.email"
            type="text"
            :placeholder="$t('general.email')"
          ></b-form-input>
        </b-form-group>

        <h5>{{ $t('users.role') }}</h5>

        <div style="font-size: 1.1em">
          <div class="flex-box">
            <div>
              <b-form-checkbox
                size="lg"
                switch
                v-model="user.isAdmin"
              ></b-form-checkbox>
            </div>
            <div style="padding-top: 2px">
              {{ $t('users.administrator') }}
            </div>
          </div>
          <small>{{ $t('users.adminRoleText') }}</small>
        </div>
      </div>

      <h5>{{ $t('users.permissions.permissions') }}</h5>
      <table class="table mb-0 table-hover">
        <thead class="thead-light">
          <tr>
            <th>{{ $t('users.sections') }}</th>
            <th class="text-center">
              <a
                @click="toggleAllHasAccess()"
                class="bx bx-toggle-left toggleHeaderBtn"
              ></a
              >{{ $t('users.permissions.hasAccess') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ $t('inventory.inventoryManagement') }}</td>
            <td class="text-center">
              <b-form-checkbox
                size="lg"
                switch
                v-model="user.permissions.hasInventoryAccess"
              ></b-form-checkbox>
            </td>
          </tr>
          <tr>
            <td>{{ $t('receptions.receptions') }}</td>
            <td class="text-center">
              <b-form-checkbox
                size="lg"
                switch
                v-model="user.permissions.hasReceptionsAccess"
              ></b-form-checkbox>
            </td>
          </tr>
          <tr>
            <td>{{ $t('orders.orders') }}</td>
            <td class="text-center">
              <b-form-checkbox
                size="lg"
                switch
                v-model="user.permissions.hasOrdersAccess"
              ></b-form-checkbox>
            </td>
          </tr>
          <tr>
            <td>{{ $t('shoppingCart.shoppingCart') }}</td>
            <td class="text-center">
              <b-form-checkbox
                size="lg"
                switch
                v-model="user.permissions.hasShoppingCartAccess"
              ></b-form-checkbox>
            </td>
          </tr>
          <tr>
            <td>{{ $t('dashboards.dashboards') }}</td>
            <td class="text-center">
              <b-form-checkbox
                size="lg"
                switch
                v-model="user.permissions.hasDashboardsAccess"
              ></b-form-checkbox>
            </td>
          </tr>
          <tr>
            <td>{{ $t('workOrders.workOrders') }}</td>
            <td class="text-center">
              <b-form-checkbox
                size="lg"
                switch
                v-model="user.permissions.hasWorkOrdersAccess"
              ></b-form-checkbox>
            </td>
          </tr>
          <tr>
            <td>{{ $t('configurations.configurations') }}</td>
            <td class="text-center">
              <b-form-checkbox
                size="lg"
                switch
                v-model="user.permissions.hasConfigAccess"
              ></b-form-checkbox>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </HeaderContentLayout>
</template>

<style scoped>
.toggleHeaderBtn {
  margin-right: 3px;
  cursor: pointer;
  font-size: 2em;
  vertical-align: bottom;
  line-height: 0.8em !important;
}
</style>